import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const CANCEL_REASONS = [
  { value: 'STUDENT_LEAVE_ADVANCE', label: 'นักเรียนแจ้งลาล่วงหน้า' },
  { value: 'STUDENT_LEAVE_SUDDEN', label: 'นักเรียนลากระทันหัน' },
  { value: 'TEACHER_LEAVE_ADVANCE', label: 'ครูลาล่วงหน้า' },
  { value: 'TEACHER_LEAVE_SUDDEN', label: 'ครูลากระทันหัน' },
  { value: 'OTHER', label: 'อื่นๆ' },
] as const;

interface CancelClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classTemplateId: string;
  eventDate: string;
  teacherCode: string;
  onSuccess?: () => void;
}

export const CancelClassDialog: React.FC<CancelClassDialogProps> = ({
  open, onOpenChange, classTemplateId, eventDate, teacherCode, onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('กรุณาเลือกเหตุผลการยกเลิก');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await (supabase as any)
        .from('v_class_template_events')
        .insert({
          class_template_id: classTemplateId,
          event_date: eventDate,
          event_type: 'CANCEL',
          cancel_reason: reason,
          note: note || null,
          requested_by: teacherCode,
          status: 'PENDING',
        });
      if (error) throw error;
      onOpenChange(false);
      setReason('');
      setNote('');
      setShowSuccess(true);
    } catch (err: any) {
      toast.error(err.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>แจ้งยกเลิกคลาส</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>เหตุผลการยกเลิก</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="เลือกเหตุผล" />
              </SelectTrigger>
              <SelectContent>
                {CANCEL_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>หมายเหตุ (อธิบายเหตุผล)</Label>
            <Textarea
              className="mt-1"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="อธิบายเหตุผลเพิ่มเติม..."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>ยกเลิก</Button>
          <Button onClick={handleSubmit} disabled={submitting}>ส่งคำขอ</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog open={showSuccess} onOpenChange={(v) => { setShowSuccess(v); if (!v) onSuccess?.(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex justify-center mb-2">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
          </div>
          <AlertDialogTitle className="text-center">ได้รับคำขอยกเลิกคลาสแล้ว</AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            โปรดรอการ approve จากทีมงาน
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-center">
          <AlertDialogAction onClick={() => { setShowSuccess(false); onSuccess?.(); }}>ตกลง</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
